import { QueryClient, QueryKey } from '@tanstack/react-query';

/**
 * Optimistically replace a single item (matched by `id`) inside a cached list,
 * and return a `rollback()` that restores the exact previous cache snapshot.
 *
 * Usage:
 *   const rollback = optimisticUpdateListItem(queryClient, key, nextItem);
 *   try { await save(); } catch { rollback(); }
 *
 * If the cache holds no list (undefined), the update is a no-op and rollback
 * restores `undefined`.
 */
export function optimisticUpdateListItem<T extends { id: number | string }>(
  queryClient: QueryClient,
  queryKey: QueryKey,
  item: T,
  merge?: (existing: T, incoming: T) => T
): () => void {
  const previous = queryClient.getQueryData<T[]>(queryKey);

  queryClient.setQueryData<T[] | undefined>(queryKey, (old) =>
    old?.map((existing) => (existing.id === item.id ? (merge ? merge(existing, item) : item) : existing))
  );

  return () => {
    queryClient.setQueryData(queryKey, previous);
  };
}
